import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  input,
} from '@angular/core'

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms'
import { UserProfileType } from '@features/user-customers/users/users.enums'
import { DEFAULT_VALUE_STEP, SECONDS_IN_1HR } from '@shared/components/stepper/stepper.constants'
import { ApplicationAccess } from '@features/user-customers/application-access-table/application-access.model'
import { now } from '@core/utils/main'
import { BehaviorSubject } from 'rxjs'
import { Switch } from '@performance/ui'
import { Calendar } from '@shared/components/calendar/calendar'
import { Stepper } from '@shared/components/stepper/stepper'
import { AsyncPipe } from '@angular/common'
import { TranslatePipe } from '@ngx-translate/core'

@Component({
  selector: 'bo-application-access-table',
  templateUrl: './application-access-table.html',
  styleUrls: ['./application-access-table.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ApplicationAccessTable),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ApplicationAccessTable),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Switch, FormsModule, Calendar, Stepper, AsyncPipe, TranslatePipe],
})
export class ApplicationAccessTable implements ControlValueAccessor, OnChanges, Validator {
  readonly #cdr: ChangeDetectorRef = inject(ChangeDetectorRef)
  readonly options = input<
    {
      label: string
      value: string
      disabled?: boolean
      license?: any
      expirationDate?: string
    }[]
  >([])
  applications: ApplicationAccess[] = []
  selectedIds: string[] = []
  isDisabled = false
  currentDate: Date = now
  language$: BehaviorSubject<string>

  onChange = (_: string[]) => {}
  onTouched = () => {}

  validate(control: AbstractControl): ValidationErrors | null {
    const selectedProfiles = control.value

    if (!Array.isArray(selectedProfiles)) {
      return null
    }

    const desktopProfile = selectedProfiles.find(
      (p: any) => p.id === UserProfileType.DesktopUserProfile,
    )

    if (desktopProfile) {
      const expiresAt = desktopProfile.license?.expiresAt
      if (!expiresAt || expiresAt === '') {
        return { requiredExpirationDate: true }
      }
    }

    return null
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.options()) {
      this.buildApplicationsList()
    }
  }

  writeValue(ids: any[]): void {
    this.selectedIds = (ids || []).map((v) => (typeof v === 'string' ? v : v.id))
    this.applications = []
    this.buildApplicationsList()
    this.#cdr.detectChanges()
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled
    this.#cdr.detectChanges()
  }

  private buildApplicationsList() {
    const options = this.options()
    if (!options || options.length === 0) {
      return
    }

    this.applications = options.map((opt) => {
      const existingApp = this.applications.find((a) => a.id === opt.value)
      const isDesktopApp = opt.value === UserProfileType.DesktopUserProfile
      const isEnabled = this.selectedIds.includes(opt.value)
      const shouldExpand =
        isDesktopApp && isEnabled && (!existingApp?.enabled || existingApp.isExpanded)

      const hasLicense = !!opt?.license
      if (isDesktopApp && !opt?.license) {
        opt.license = {
          expiresAt: null,
          features: { refereeMode: false },
          tokenDuration: DEFAULT_VALUE_STEP * SECONDS_IN_1HR,
        }
      }
      const tokenDurationInHours =
        opt?.license?.tokenDuration > 0
          ? opt.license.tokenDuration / SECONDS_IN_1HR
          : DEFAULT_VALUE_STEP

      return {
        id: opt.value,
        name: opt.label,
        enabled: isEnabled,
        isExpanded: shouldExpand,
        isDesktop: isDesktopApp,
        hasLicense,
        license: opt.license,
        offlineHours: tokenDurationInHours,
      }
    })

    this.#cdr.detectChanges()
  }

  onSelectionChange() {
    const selectedProfiles = this.applications
      .filter((app) => app.enabled)
      .map((app) => {
        const profilePayload: any = { id: app.id }

        if (app.isDesktop && app.license) {
          profilePayload.license = app.license
        }

        return profilePayload
      })

    this.onTouched()
    this.onChange(selectedProfiles)
  }

  onAppStatusChange(newStatus: boolean, app: ApplicationAccess) {
    app.enabled = newStatus
    if (app.isDesktop) {
      app.isExpanded = app.enabled
    }
    this.onSelectionChange()
  }

  updateDesktopRefereeMode(newStatus: boolean, app: ApplicationAccess) {
    app.license.features.refereeMode = newStatus
    this.onSelectionChange()
  }

  updateDesktopOfflineHours(tokenDuration: number, app: ApplicationAccess) {
    app.offlineHours = tokenDuration
    app.license.tokenDuration = tokenDuration * SECONDS_IN_1HR
    this.onSelectionChange()
  }

  updateExpirationDate(date: string | Date, app: ApplicationAccess) {
    const newDate = new Date(date)
    const oldDateStr = app.license.expiresAt

    if (!oldDateStr) {
      newDate.setHours(23, 59, 0, 0)
    } else {
      const oldDate = new Date(oldDateStr)

      const isDateChanged =
        oldDate.getFullYear() !== newDate.getFullYear() ||
        oldDate.getMonth() !== newDate.getMonth() ||
        oldDate.getDate() !== newDate.getDate()

      if (isDateChanged) {
        newDate.setHours(23, 59, 0, 0)
      }
    }

    app.license.expiresAt = newDate.toISOString()
    this.onSelectionChange()
  }
}
